import { withSearchFilter, type FilterTree } from '@/app/components/query-builder/grouped-query';
import { ListResponse } from '@/types/common';
import fetchWithAuth from './base';
import {
  SignalContactSummary,
  SignalDateGroup,
  MarketSignalItemData,
  MarketSignalDateGroup,
  WebsiteSignalDateGroup,
  WebsiteSignalItemData,
  LinkedInSignalItemData,
  LinkedInSignalDateGroup,
} from '@/types/signals';

export type SignalGraphGranularity = 'daily' | 'monthly';

export interface SignalGraphResponse {
  granularity: SignalGraphGranularity;
  start_date: string;
  end_date: string;
  points: Array<{
    period: string;
    values: { website: number; market: number; linkedin: number; [category: string]: number };
    total: number;
  }>;
}

export const getSignalGraph = async (
  clientId: string,
  granularity: SignalGraphGranularity
): Promise<SignalGraphResponse> => {
  const params = new URLSearchParams({ granularity });
  const response = await fetchWithAuth(
    `/api/client/${clientId}/signal/graph?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch signal graph', { cause: data });
  }

  return data;
};

export const getContactWebsiteSignals = async ({
  clientId,
  contactId,
  startingAfter,
  limit,
  filters,
  match,
}: {
  clientId: string;
  contactId: string;
  startingAfter?: string;
  limit?: number;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
}): Promise<ListResponse<WebsiteSignalDateGroup>> => {
  const params = new URLSearchParams();
  if (startingAfter) params.set('starting_after', startingAfter);
  if (limit) params.set('limit', limit.toString());

  const hasFilters = !!filters?.length;
  const response = hasFilters
    ? await fetchWithAuth(`/api/client/${clientId}/contact/${contactId}/website_signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          ...(limit ? { limit } : {}),
          ...(filters?.length ? { filter: { match: match || 'all', rules: filters } } : {}),
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/${contactId}/website_signal${params.size ? `?${params.toString()}` : ''}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch contact website signals', {
      cause: data,
    });
  }

  return data;
};

export const getContactMarketSignals = async ({
  clientId,
  contactId,
  startingAfter,
  limit,
  filters,
  match,
}: {
  clientId: string;
  contactId: string;
  startingAfter?: string;
  limit?: number;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
}): Promise<ListResponse<MarketSignalDateGroup>> => {
  const params = new URLSearchParams();
  if (startingAfter) params.set('starting_after', startingAfter);
  if (limit) params.set('limit', limit.toString());

  const response = filters?.length
    ? await fetchWithAuth(`/api/client/${clientId}/contact/${contactId}/market_signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          ...(limit ? { limit } : {}),
          filter: { match: match || 'all', rules: filters },
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/${contactId}/market_signal${params.size ? `?${params.toString()}` : ''}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch contact market signals', {
      cause: data,
    });
  }

  return data;
};

export const getContactLinkedInSignals = async ({
  clientId,
  contactId,
  startingAfter,
  limit,
  filters,
  match,
}: {
  clientId: string;
  contactId: string;
  startingAfter?: string;
  limit?: number;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
}): Promise<ListResponse<LinkedInSignalDateGroup>> => {
  const params = new URLSearchParams();
  if (startingAfter) params.set('starting_after', startingAfter);
  if (limit) params.set('limit', limit.toString());

  const response = filters?.length
    ? await fetchWithAuth(`/api/client/${clientId}/contact/${contactId}/linkedin_signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          ...(limit ? { limit } : {}),
          filter: { match: match || 'all', rules: filters },
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/${contactId}/linkedin_signal${params.size ? `?${params.toString()}` : ''}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch contact LinkedIn signals', {
      cause: data,
    });
  }

  return data;
};

export const getWebsiteSignals = async ({
  clientId,
  startingAfter,
  limit,
  search,
  orderBy,
  filterSearchParams,
}: {
  clientId: string;
  startingAfter?: string;
  limit?: number;
  search?: string;
  orderBy?: string;
  filterSearchParams?: URLSearchParams;
}): Promise<ListResponse<WebsiteSignalItemData>> => {
  const params = new URLSearchParams(filterSearchParams || {});
  if (startingAfter) params.set('starting_after', startingAfter);
  if (limit) params.set('limit', limit.toString());
  if (search) params.set('search', search);
  if (orderBy === 'contact_created_at') params.set('order_by', orderBy);

  const query = params.toString();
  const response = await fetchWithAuth(
    `/api/client/${clientId}/contact/website_signal${query ? `?${query}` : ''}`,
    { method: 'GET', body: '' }
  );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch website signals', { cause: data });
  }

  if (Array.isArray(data)) {
    return {
      data,
      total: data.length,
      starting_after: '',
      ending_before: '',
      has_more: false,
    };
  }

  return data;
};

export const getMarketSignals = async ({
  clientId,
  startingAfter,
  limit,
  search,
  orderBy,
  filters,
  match,
  filter,
}: {
  clientId: string;
  startingAfter?: string;
  limit?: number;
  search?: string;
  orderBy?: string;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
  filter?: FilterTree;
}): Promise<ListResponse<SignalContactSummary>> => {
  const hasFilterTree = !!filter?.rules.length;
  const requestFilters = hasFilterTree
    ? filters || []
    : [...(filters || []), ...(search ? [{ field: 'search', operator: 'eq', value: search }] : [])];

  const hasCustomSort = orderBy === 'contact_created_at';
  const shouldUseSearch = hasFilterTree || requestFilters.length > 0 || hasCustomSort;
  const response = shouldUseSearch
    ? await fetchWithAuth(`/api/client/${clientId}/contact/market_signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: limit || 25,
          ...(hasFilterTree
            ? { filter: withSearchFilter(filter, search) }
            : {
                ...(requestFilters.length
                  ? { filter: { match: match || 'all', rules: requestFilters } }
                  : {}),
              }),
          ...(hasCustomSort ? { sort: [{ field: orderBy, direction: 'desc' }] } : {}),
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/market_signal?${new URLSearchParams({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: String(limit || 25),
        }).toString()}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch market signals', { cause: data });
  }

  const responseData: MarketSignalItemData[] = Array.isArray(data) ? data : data.data || [];
  const mappedData: SignalContactSummary[] = responseData.map(item => {
    const { contact, market_signal_summary: summary } = item;
    const contactInfo = contact.contact_info;
    const company = contact.company_info;
    const evaluation = contact.eval;
    const companyName = company.name || company.full_name;
    const lastSignal = summary.last_signal;

    return {
      id: contact.id,
      client_id: contact.client_id,
      company_id: company.company_id,
      persona_id: evaluation.persona_id,
      eval: evaluation,
      attribution: contact.attribution,
      first_name: contactInfo.name.first,
      last_name: contactInfo.name.last,
      title: contactInfo.role.title,
      photo_url: contactInfo.photo_url,
      linkedin: contactInfo.social.linkedin,
      company_name: companyName,
      company_logo: company.logo,
      persona_name: evaluation.persona_name,
      icp_name: evaluation.icp_name,
      signal: {
        id: lastSignal.id,
        client_id: contact.client_id,
        company_id: company.company_id,
        company_name: companyName,
        company_linkedin: company.social?.linkedin || '',
        icp_id: evaluation.icp_id,
        icp_short_name: evaluation.icp_name,
        action: lastSignal.action || lastSignal.market.trigger || '-',
        entity: lastSignal.type || 'market_signal',
        signal_at: lastSignal.signal_at || lastSignal.captured_at || lastSignal.created_at,
        contact: {
          id: contact.id,
          persona_id: evaluation.persona_id,
          persona_short_name: evaluation.persona_name,
          first_name: contactInfo.name.first,
          last_name: contactInfo.name.last,
          photo_url: contactInfo.photo_url,
          linkedin: contactInfo.social.linkedin,
        },
        message: null,
        page: null,
        market_signal: lastSignal.market,
        provider: lastSignal.provider || lastSignal.market.provider,
        created_at: lastSignal.created_at,
        updated_at: lastSignal.updated_at,
      },
      total_signals: summary.total_count,
      status: contact.status,
      created_at: contact.created_at,
      updated_at: contact.updated_at,
    };
  });

  return {
    data: mappedData,
    total: data.total ?? mappedData.length,
    starting_after: data.starting_after ?? '',
    ending_before: data.ending_before ?? '',
    has_more: data.has_more ?? false,
  };
};

export const getLinkedInSignals = async ({
  clientId,
  startingAfter,
  limit,
  search,
  orderBy,
  filters,
  match,
  filter,
}: {
  clientId: string;
  startingAfter?: string;
  limit?: number;
  search?: string;
  orderBy?: string;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
  filter?: FilterTree;
}): Promise<ListResponse<SignalContactSummary>> => {
  const hasFilterTree = !!filter?.rules.length;
  const requestFilters = hasFilterTree
    ? filters || []
    : [...(filters || []), ...(search ? [{ field: 'search', operator: 'eq', value: search }] : [])];

  const hasCustomSort = orderBy === 'contact_created_at';
  const shouldUseSearch = hasFilterTree || requestFilters.length > 0 || hasCustomSort;
  const response = shouldUseSearch
    ? await fetchWithAuth(`/api/client/${clientId}/contact/linkedin_signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: limit || 25,
          ...(hasFilterTree
            ? { filter: withSearchFilter(filter, search) }
            : {
                ...(requestFilters.length
                  ? { filter: { match: match || 'all', rules: requestFilters } }
                  : {}),
              }),
          ...(hasCustomSort ? { sort: [{ field: orderBy, direction: 'desc' }] } : {}),
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/linkedin_signal?${new URLSearchParams({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: String(limit || 25),
        }).toString()}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch LinkedIn signals', { cause: data });
  }

  const responseData: LinkedInSignalItemData[] = Array.isArray(data) ? data : data.data || [];
  const mappedData: SignalContactSummary[] = responseData.flatMap(item => {
    const { contact, linkedin_signal_summary: summary } = item;
    // The API can include contacts with a zero-count summary and no last signal.
    // They have no activity to render in the LinkedIn signals list.
    if (!summary?.last_signal) return [];
    const contactInfo = contact.contact_info;
    const company = contact.company_info;
    const evaluation = contact.eval;
    const companyName = company.name || company.full_name;
    const lastSignal = summary.last_signal;

    return [
      {
        id: contact.id,
        client_id: contact.client_id,
        company_id: company.company_id,
        persona_id: evaluation.persona_id,
        eval: evaluation,
        attribution: contact.attribution,
        first_name: contactInfo.name.first,
        last_name: contactInfo.name.last,
        title: contactInfo.role.title,
        photo_url: contactInfo.photo_url,
        linkedin: contactInfo.social.linkedin,
        company_name: companyName,
        company_logo: company.logo,
        persona_name: evaluation.persona_name,
        icp_name: evaluation.icp_name,
        signal: {
          id: lastSignal.id,
          client_id: contact.client_id,
          company_id: company.company_id,
          company_name: companyName,
          company_linkedin: company.social?.linkedin || '',
          icp_id: evaluation.icp_id,
          icp_short_name: evaluation.icp_name,
          action: lastSignal.trigger || lastSignal.linkedin_signal?.trigger || '-',
          trigger: lastSignal.trigger || lastSignal.linkedin_signal?.trigger,
          reaction_type: lastSignal.linkedin_signal?.reaction_type,
          entity: lastSignal.type || 'linkedin',
          signal_at: lastSignal.signal_at || lastSignal.captured_at || lastSignal.created_at,
          contact: {
            id: contact.id,
            persona_id: evaluation.persona_id,
            persona_short_name: evaluation.persona_name,
            first_name: contactInfo.name.first,
            last_name: contactInfo.name.last,
            photo_url: contactInfo.photo_url,
            linkedin: contactInfo.social.linkedin,
          },
          message: null,
          page: null,
          linkedin_signal: lastSignal.linkedin_signal,
          created_at: lastSignal.created_at,
          updated_at: lastSignal.updated_at,
        },
        total_signals: summary.total_count,
        status: contact.status,
        created_at: contact.created_at,
        updated_at: contact.updated_at,
      },
    ];
  });

  return {
    data: mappedData,
    total: data.total ?? mappedData.length,
    starting_after: data.starting_after ?? '',
    ending_before: data.ending_before ?? '',
    has_more: data.has_more ?? false,
  };
};

export const updateSignal = async (id: string, clientId: string, data: any) => {
  const resource = data?.resource === 'contact' ? 'contact' : 'company';
  const payload =
    data && typeof data === 'object' && 'resource' in data
      ? Object.fromEntries(Object.entries(data).filter(([key]) => key !== 'resource'))
      : data;

  const response = await fetchWithAuth(`/api/client/${clientId}/${resource}/signal/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const getCompanySignals = async ({
  clientId,
  companyId,
  startingAfter,
  filterSearchParams,
  limit = 20,
}: {
  clientId: string;
  companyId: string;
  startingAfter?: string;
  filterSearchParams?: URLSearchParams;
  limit?: number;
}): Promise<ListResponse<SignalDateGroup>> => {
  const params = new URLSearchParams(filterSearchParams || {});
  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }
  if (limit) {
    params.set('limit', limit.toString());
  }

  const startDate = params.get('sd_signal_at_start');
  const endDate = params.get('sd_signal_at_end');

  if (startDate && endDate) {
    params.set('signal_at_start', startDate);
    params.set('signal_at_end', endDate);

    params.delete('sd_signal_at_start');
    params.delete('sd_signal_at_end');
  }

  if (params.has('sd_action')) {
    params.set('action', params.get('sd_action')!);
    params.delete('sd_action');
  }
  if (params.has('sd_entity')) {
    params.set('entity', params.get('sd_entity')!);
    params.delete('sd_entity');
  }
  if (params.has('sd_contact_id')) {
    params.set('contact_id', params.get('sd_contact_id')!);
    params.delete('sd_contact_id');
  }

  const response = await fetchWithAuth(
    `/api/client/${clientId}/company/${companyId}/signal?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch company signals', { cause: data });
  }

  return data;
};

export const getContactSignals = async ({
  clientId,
  startingAfter,
  filterSearchParams,
  limit,
  search,
  orderBy,
  filters,
  match,
  filter,
}: {
  clientId: string;
  startingAfter?: string;
  filterSearchParams?: URLSearchParams;
  limit?: number;
  search?: string;
  orderBy?: string;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
  match?: 'all' | 'any';
  filter?: FilterTree;
  targetPages?: Array<{
    conditions: Array<{ field: string; operator: string; value: unknown }>;
  }>;
}): Promise<ListResponse<SignalContactSummary | SignalDateGroup>> => {
  void filterSearchParams;
  const requestFilters = [
    ...(filters || []),
    ...(search ? [{ field: 'search', operator: 'eq', value: search }] : []),
  ];

  const hasCustomSort = orderBy === 'contact_created_at';
  const hasFilterTree = !!filter?.rules.length;
  const shouldUseSearch = hasFilterTree || requestFilters.length > 0 || hasCustomSort;
  const response = shouldUseSearch
    ? await fetchWithAuth(`/api/client/${clientId}/contact/website_signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: limit || 25,
          ...(hasFilterTree
            ? {
                filter: withSearchFilter(filter, search),
              }
            : {
                ...(requestFilters.length
                  ? { filter: { match: match || 'all', rules: requestFilters } }
                  : {}),
              }),
          ...(hasCustomSort ? { sort: [{ field: orderBy, direction: 'desc' }] } : {}),
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/website_signal?${new URLSearchParams({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: String(limit || 25),
        }).toString()}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch contact signals', { cause: data });
  }

  const responseData: WebsiteSignalItemData[] = Array.isArray(data) ? data : data.data || [];
  const mappedData: SignalContactSummary[] = responseData.map(item => {
    const { contact, website_signal_summary: summary } = item;
    const contactInfo = contact.contact_info;
    const company = contact.company_info;
    const evaluation = contact.eval;
    const companyName = company.name || company.full_name;
    const lastSignal = summary.last_signal;
    const websiteSignal = lastSignal.website;
    const targetPage = websiteSignal.target_page;

    return {
      id: contact.id,
      client_id: contact.client_id,
      company_id: company.company_id,
      persona_id: evaluation.persona_id,
      eval: evaluation,
      attribution: contact.attribution,
      first_name: contactInfo.name.first,
      last_name: contactInfo.name.last,
      title: contactInfo.role.title,
      photo_url: contactInfo.photo_url,
      linkedin: contactInfo.social.linkedin,
      company_name: companyName,
      company_logo: company.logo,
      persona_name: evaluation.persona_name,
      icp_name: evaluation.icp_name,
      signal: {
        id: lastSignal.id,
        client_id: contact.client_id,
        company_id: company.company_id,
        company_name: companyName,
        company_linkedin: company.social?.linkedin || '',
        icp_id: evaluation.icp_id,
        icp_short_name: evaluation.icp_name,
        action: websiteSignal.trigger || '-',
        entity: lastSignal.type || 'website_signal',
        signal_at: lastSignal.captured_at || lastSignal.created_at,
        contact: {
          id: contact.id,
          persona_id: evaluation.persona_id,
          persona_short_name: evaluation.persona_name,
          first_name: contactInfo.name.first,
          last_name: contactInfo.name.last,
          photo_url: contactInfo.photo_url,
          linkedin: contactInfo.social.linkedin,
        },
        message: null,
        page: {
          url: websiteSignal.url.raw,
          time_spent: websiteSignal.session.duration_secs,
          target_page_id: targetPage?.id || '',
          target_page_short_name: targetPage?.short_name || targetPage?.name || '',
        },
        created_at: lastSignal.created_at,
        updated_at: lastSignal.updated_at,
      },
      total_signals: summary.total_count,
      status: contact.status,
      created_at: contact.created_at,
      updated_at: contact.updated_at,
    };
  });

  return {
    data: mappedData,
    total: data.total ?? mappedData.length,
    starting_after: data.starting_after ?? '',
    ending_before: data.ending_before ?? '',
    has_more: data.has_more ?? false,
  };
};

type SignalCountResponse = { total: number; persona?: number } | null;

const getSignalCount = async (url: string): Promise<SignalCountResponse> => {
  try {
    const response = await fetchWithAuth(url, { method: 'GET' });
    const data = await response.json();
    return typeof data?.total === 'number' ? data : null;
  } catch {
    return null;
  }
};

export const getWebsiteSignalCount = (clientId: string): Promise<SignalCountResponse> =>
  getSignalCount(`/api/client/${clientId}/contact/website_signal/count`);

export const getMarketSignalCount = (clientId: string): Promise<SignalCountResponse> =>
  getSignalCount(`/api/client/${clientId}/contact/market_signal/count`);

export const getLinkedInSignalCount = (clientId: string): Promise<SignalCountResponse> =>
  getSignalCount(`/api/client/${clientId}/contact/linkedin_signal/count`);

export const getAllSignalCount = (clientId: string): Promise<SignalCountResponse> =>
  getSignalCount(`/api/client/${clientId}/contact/signal/count`);

type AggregateSignalSummary = {
  total_count: number;
  last_signal: {
    id: string;
    type: string;
    captured_at?: string;
    created_at: string;
    updated_at: string;
    website: WebsiteSignalItemData['website_signal_summary']['last_signal']['website'] | null;
    market: MarketSignalItemData['market_signal_summary']['last_signal']['market'] | null;
    linkedin:
      | NonNullable<
          LinkedInSignalItemData['linkedin_signal_summary']['last_signal']
        >['linkedin_signal']
      | null;
  };
};

type AllSignalItemData =
  | WebsiteSignalItemData
  | MarketSignalItemData
  | LinkedInSignalItemData
  | {
      contact: WebsiteSignalItemData['contact'];
      signal_summary: AggregateSignalSummary;
    };

const mapAllSignalItem = (item: AllSignalItemData): SignalContactSummary[] => {
  if ('signal_summary' in item) {
    const lastSignal = item.signal_summary.last_signal;
    if (!lastSignal) return [];

    if (lastSignal.type === 'website' && lastSignal.website) {
      return mapAllSignalItem({
        contact: item.contact,
        website_signal_summary:
          item.signal_summary as WebsiteSignalItemData['website_signal_summary'],
      });
    }

    if (lastSignal.type === 'market' && lastSignal.market) {
      return mapAllSignalItem({
        contact: item.contact,
        market_signal_summary: item.signal_summary as MarketSignalItemData['market_signal_summary'],
      });
    }

    if (lastSignal.type !== 'linkedin' || !lastSignal.linkedin) return [];

    return mapAllSignalItem({
      contact: item.contact,
      linkedin_signal_summary: {
        total_count: item.signal_summary.total_count,
        last_signal: {
          ...lastSignal,
          linkedin_signal: lastSignal.linkedin,
        },
      },
    });
  }

  if ('website_signal_summary' in item) {
    const { contact, website_signal_summary: summary } = item;
    const contactInfo = contact.contact_info;
    const company = contact.company_info;
    const evaluation = contact.eval;
    const companyName = company.name || company.full_name;
    const lastSignal = summary.last_signal;
    const websiteSignal = lastSignal.website;
    const targetPage = websiteSignal.target_page;

    return [
      {
        id: contact.id,
        client_id: contact.client_id,
        company_id: company.company_id,
        persona_id: evaluation.persona_id,
        eval: evaluation,
        attribution: contact.attribution,
        first_name: contactInfo.name.first,
        last_name: contactInfo.name.last,
        title: contactInfo.role.title,
        photo_url: contactInfo.photo_url,
        linkedin: contactInfo.social.linkedin,
        company_name: companyName,
        company_logo: company.logo,
        persona_name: evaluation.persona_name,
        icp_name: evaluation.icp_name,
        signal: {
          id: lastSignal.id,
          client_id: contact.client_id,
          company_id: company.company_id,
          company_name: companyName,
          company_linkedin: company.social?.linkedin || '',
          icp_id: evaluation.icp_id,
          icp_short_name: evaluation.icp_name,
          action: websiteSignal.trigger || '-',
          entity: lastSignal.type || 'website_signal',
          signal_at: lastSignal.captured_at || lastSignal.created_at,
          contact: {
            id: contact.id,
            persona_id: evaluation.persona_id,
            persona_short_name: evaluation.persona_name,
            first_name: contactInfo.name.first,
            last_name: contactInfo.name.last,
            photo_url: contactInfo.photo_url,
            linkedin: contactInfo.social.linkedin,
          },
          message: null,
          page: {
            url: websiteSignal.url.raw,
            time_spent: websiteSignal.session.duration_secs,
            target_page_id: targetPage?.id || '',
            target_page_short_name: targetPage?.short_name || targetPage?.name || '',
          },
          created_at: lastSignal.created_at,
          updated_at: lastSignal.updated_at,
        },
        total_signals: summary.total_count,
        status: contact.status,
        created_at: contact.created_at,
        updated_at: contact.updated_at,
      },
    ];
  }

  if ('market_signal_summary' in item) {
    const { contact, market_signal_summary: summary } = item;
    const contactInfo = contact.contact_info;
    const company = contact.company_info;
    const evaluation = contact.eval;
    const companyName = company.name || company.full_name;
    const lastSignal = summary.last_signal;

    return [
      {
        id: contact.id,
        client_id: contact.client_id,
        company_id: company.company_id,
        persona_id: evaluation.persona_id,
        eval: evaluation,
        attribution: contact.attribution,
        first_name: contactInfo.name.first,
        last_name: contactInfo.name.last,
        title: contactInfo.role.title,
        photo_url: contactInfo.photo_url,
        linkedin: contactInfo.social.linkedin,
        company_name: companyName,
        company_logo: company.logo,
        persona_name: evaluation.persona_name,
        icp_name: evaluation.icp_name,
        signal: {
          id: lastSignal.id,
          client_id: contact.client_id,
          company_id: company.company_id,
          company_name: companyName,
          company_linkedin: company.social?.linkedin || '',
          icp_id: evaluation.icp_id,
          icp_short_name: evaluation.icp_name,
          action: lastSignal.action || lastSignal.market.trigger || '-',
          entity: lastSignal.type || 'market_signal',
          signal_at: lastSignal.signal_at || lastSignal.captured_at || lastSignal.created_at,
          contact: {
            id: contact.id,
            persona_id: evaluation.persona_id,
            persona_short_name: evaluation.persona_name,
            first_name: contactInfo.name.first,
            last_name: contactInfo.name.last,
            photo_url: contactInfo.photo_url,
            linkedin: contactInfo.social.linkedin,
          },
          message: null,
          page: null,
          market_signal: lastSignal.market,
          provider: lastSignal.provider || lastSignal.market.provider,
          created_at: lastSignal.created_at,
          updated_at: lastSignal.updated_at,
        },
        total_signals: summary.total_count,
        status: contact.status,
        created_at: contact.created_at,
        updated_at: contact.updated_at,
      },
    ];
  }

  const { contact, linkedin_signal_summary: summary } = item;
  if (!summary?.last_signal) return [];
  const contactInfo = contact.contact_info;
  const company = contact.company_info;
  const evaluation = contact.eval;
  const companyName = company.name || company.full_name;
  const lastSignal = summary.last_signal;

  return [
    {
      id: contact.id,
      client_id: contact.client_id,
      company_id: company.company_id,
      persona_id: evaluation.persona_id,
      eval: evaluation,
      attribution: contact.attribution,
      first_name: contactInfo.name.first,
      last_name: contactInfo.name.last,
      title: contactInfo.role.title,
      photo_url: contactInfo.photo_url,
      linkedin: contactInfo.social.linkedin,
      company_name: companyName,
      company_logo: company.logo,
      persona_name: evaluation.persona_name,
      icp_name: evaluation.icp_name,
      signal: {
        id: lastSignal.id,
        client_id: contact.client_id,
        company_id: company.company_id,
        company_name: companyName,
        company_linkedin: company.social?.linkedin || '',
        icp_id: evaluation.icp_id,
        icp_short_name: evaluation.icp_name,
        action: lastSignal.trigger || lastSignal.linkedin_signal?.trigger || '-',
        trigger: lastSignal.trigger || lastSignal.linkedin_signal?.trigger,
        reaction_type: lastSignal.linkedin_signal?.reaction_type,
        entity: lastSignal.type || 'linkedin',
        signal_at: lastSignal.signal_at || lastSignal.captured_at || lastSignal.created_at,
        contact: {
          id: contact.id,
          persona_id: evaluation.persona_id,
          persona_short_name: evaluation.persona_name,
          first_name: contactInfo.name.first,
          last_name: contactInfo.name.last,
          photo_url: contactInfo.photo_url,
          linkedin: contactInfo.social.linkedin,
        },
        message: null,
        page: null,
        linkedin_signal: lastSignal.linkedin_signal,
        created_at: lastSignal.created_at,
        updated_at: lastSignal.updated_at,
      },
      total_signals: summary.total_count,
      status: contact.status,
      created_at: contact.created_at,
      updated_at: contact.updated_at,
    },
  ];
};

export const getAllSignals = async ({
  clientId,
  startingAfter,
  limit,
  search,
  orderBy,
  filter,
}: {
  clientId: string;
  startingAfter?: string;
  limit?: number;
  search?: string;
  orderBy?: string;
  filter?: FilterTree;
}): Promise<ListResponse<SignalContactSummary | SignalDateGroup>> => {
  const hasCustomSort = orderBy === 'contact_created_at';
  const requestFilter = withSearchFilter(filter ?? { match: 'all', rules: [] }, search);
  const shouldUseSearch = !!requestFilter?.rules.length || hasCustomSort;
  const response = shouldUseSearch
    ? await fetchWithAuth(`/api/client/${clientId}/contact/signal/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: limit || 25,
          ...(requestFilter?.rules.length ? { filter: requestFilter } : {}),
          ...(hasCustomSort ? { sort: [{ field: orderBy, direction: 'desc' }] } : {}),
        }),
      })
    : await fetchWithAuth(
        `/api/client/${clientId}/contact/signal?${new URLSearchParams({
          ...(startingAfter ? { starting_after: startingAfter } : {}),
          limit: String(limit || 25),
        }).toString()}`,
        { method: 'GET' }
      );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch signals', { cause: data });
  }

  const responseData: AllSignalItemData[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const normalizedData = responseData.flatMap(mapAllSignalItem);

  return {
    data: normalizedData,
    total: typeof data?.total === 'number' ? data.total : normalizedData.length,
    starting_after: data.starting_after ?? '',
    ending_before: data.ending_before ?? '',
    has_more: data.has_more ?? false,
  };
};

export const getContactTimelineSignals = async ({
  clientId,
  contactId,
  startingAfter,
  filterSearchParams,
  limit = 20,
}: {
  clientId: string;
  contactId: string;
  startingAfter?: string;
  filterSearchParams?: URLSearchParams;
  limit?: number;
}): Promise<ListResponse<SignalDateGroup>> => {
  const params = new URLSearchParams(filterSearchParams || {});

  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (limit) {
    params.set('limit', limit.toString());
  }

  const startDate = params.get('sd_signal_at_start');
  const endDate = params.get('sd_signal_at_end');

  if (startDate && endDate) {
    params.set('signal_at_start', startDate);
    params.set('signal_at_end', endDate);

    params.delete('sd_signal_at_start');
    params.delete('sd_signal_at_end');
  }

  if (params.has('sd_action')) {
    params.set('action', params.get('sd_action')!);
    params.delete('sd_action');
  }
  if (params.has('sd_entity')) {
    params.set('entity', params.get('sd_entity')!);
    params.delete('sd_entity');
  }

  const response = await fetchWithAuth(
    `/api/client/${clientId}/contact/${contactId}/signal?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  const data = await response.json();

  if (data?.status === 0 || data?.error) {
    throw new Error(data?.error?.message || 'Failed to fetch contact signals', { cause: data });
  }

  return data;
};
